import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const EMPTY = { name: "", city: "", program: "", rating: 5, content: "", photo_url: "", status: "published" };

export default function AdminTestimoni() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => axios.get(`${API}/admin/testimoni`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm(t); setModalOpen(true); };
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    try {
      const payload = { ...form, rating: Number(form.rating) };
      if (editing) await axios.put(`${API}/admin/testimoni/${editing.id}`, payload);
      else await axios.post(`${API}/admin/testimoni`, payload);
      toast.success("Testimoni berhasil disimpan");
      setModalOpen(false);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/testimoni/${deleteId}`);
      toast.success("Testimoni berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-testimoni-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Testimoni</h1>
          <p className="text-sm text-[#64748B]">Kelola testimoni jamaah yang tampil di website.</p>
        </div>
        <Button data-testid="admin-testimoni-add-button" onClick={openCreate} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Tambah Testimoni</Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={Star} title="Belum ada testimoni" description="Tambahkan testimoni jamaah pertama Anda." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-testimoni-table">
            <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Kota</TableHead><TableHead>Rating</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`admin-testimoni-row-${t.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{t.name}</TableCell>
                  <TableCell className="text-[#64748B]">{t.city}</TableCell>
                  <TableCell className="text-[#64748B]">{t.rating} / 5</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" data-testid={`admin-testimoni-edit-${t.id}`} onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`admin-testimoni-delete-${t.id}`} onClick={() => setDeleteId(t.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="admin-testimoni-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Testimoni" : "Tambah Testimoni"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nama</Label><Input data-testid="testimonial-form-name" value={form.name} onChange={(e) => update("name", e.target.value)} /></div>
              <div><Label>Kota</Label><Input data-testid="testimonial-form-city" value={form.city} onChange={(e) => update("city", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Program</Label><Input data-testid="testimonial-form-program" value={form.program} onChange={(e) => update("program", e.target.value)} /></div>
              <div><Label>Rating (1-5)</Label><Input data-testid="testimonial-form-rating" type="number" min={1} max={5} value={form.rating} onChange={(e) => update("rating", e.target.value)} /></div>
            </div>
            <div><Label>Isi Testimoni</Label><Textarea data-testid="testimonial-form-content" value={form.content} onChange={(e) => update("content", e.target.value)} rows={3} /></div>
            <div><Label>URL Foto</Label><Input data-testid="testimonial-form-photo" value={form.photo_url} onChange={(e) => update("photo_url", e.target.value)} /></div>
            <Button data-testid="admin-testimoni-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Hapus Testimoni?" onConfirm={handleDelete} testId="admin-testimoni-delete-confirm" />
    </div>
  );
}
